import { create } from 'zustand'
import { AxiosInstance } from 'axios'
import {
  BulkRefreshOptions,
  BulkRefreshProgress,
  retryFailedBulkRefresh,
  runBulkRefresh,
} from '../lib/bulkRefresh'
import { queryClient } from '../queryClient'

/**
 * Stop flags live outside the store: they're transient control signals, not
 * state the UI renders.
 */
const stopFlags = new Map<string, boolean>()

function isRunActive(run?: BulkRefreshProgress): boolean {
  return run?.status === 'collecting' || run?.status === 'running'
}

interface BulkRefreshStoreState {
  runs: Record<string, BulkRefreshProgress>
  startBulkRefresh: (
    client: AxiosInstance,
    albumId: string,
    options?: BulkRefreshOptions
  ) => Promise<BulkRefreshProgress | null>
  retryFailedImages: (
    client: AxiosInstance,
    albumId: string,
    options?: BulkRefreshOptions
  ) => Promise<BulkRefreshProgress | null>
  stopBulkRefresh: (albumId: string) => void
  clearRun: (albumId: string) => void
}

export const useBulkRefreshStore = create<BulkRefreshStoreState>()((set, get) => {
  const setRun = (albumId: string, progress: BulkRefreshProgress) => {
    set((state) => ({ runs: { ...state.runs, [albumId]: progress } }))
  }

  return {
    runs: {},

    startBulkRefresh: async (client, albumId, options = {}) => {
      if (isRunActive(get().runs[albumId])) {
        return null
      }

      stopFlags.set(albumId, false)

      const final = await runBulkRefresh(client, albumId, {
        ...options,
        shouldStop: () => stopFlags.get(albumId) === true,
        onProgress: (progress) => setRun(albumId, progress),
      })

      stopFlags.delete(albumId)

      if (final.status === 'completed' || final.done > 0 || final.skipped > 0) {
        queryClient.invalidateQueries({ queryKey: ['images', 'admin'] })
        queryClient.invalidateQueries({ queryKey: ['album', 'admin', albumId] })
      }

      return final
    },

    retryFailedImages: async (client, albumId, options = {}) => {
      const previous = get().runs[albumId]
      if (!previous || isRunActive(previous)) {
        return null
      }

      const hasFailures = Object.values(previous.imageStatuses).some(
        (status) => status === 'failed'
      )
      if (!hasFailures) {
        return null
      }

      stopFlags.set(albumId, false)

      const final = await retryFailedBulkRefresh(client, previous, {
        ...options,
        shouldStop: () => stopFlags.get(albumId) === true,
        onProgress: (progress) => setRun(albumId, progress),
      })

      stopFlags.delete(albumId)

      if (final.done > 0 || final.skipped > 0) {
        queryClient.invalidateQueries({ queryKey: ['images', 'admin'] })
        queryClient.invalidateQueries({ queryKey: ['album', 'admin', albumId] })
      }

      return final
    },

    stopBulkRefresh: (albumId) => {
      stopFlags.set(albumId, true)
    },

    clearRun: (albumId) => {
      set((state) => {
        const runs = { ...state.runs }
        delete runs[albumId]
        return { runs }
      })
      stopFlags.delete(albumId)
    },
  }
})
